import React, { useState } from 'react';
import { X, Copy, Clock, Sparkles, Edit3, Wand2, RotateCcw } from 'lucide-react';
import { Prompt, PromptHistoryEntry } from '../../types'; // Keep this import
import { useClickOutside } from '../../hooks/useClickOutside';
import { useScrollLock } from '../../hooks/useScrollLock';

interface PromptHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  prompt: Prompt;
  onRestoreVersion: (historyEntry: PromptHistoryEntry) => void;
}

export const PromptHistoryModal: React.FC<PromptHistoryModalProps> = ({
  isOpen,
  onClose,
  prompt,
  onRestoreVersion,
}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const modalRef = useClickOutside(() => {
    if (isOpen) {
      onClose();
    }
  });

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleRestore = (historyEntry: PromptHistoryEntry) => {
    if (window.confirm('Are you sure you want to restore this version? This will replace the current prompt content.')) {
      onRestoreVersion(historyEntry);
      onClose();
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'enhanced':
        return <Sparkles className="h-4 w-4 text-blue-500" />;
      case 'variation':
        return <Wand2 className="h-4 w-4 text-purple-500" />;
      case 'manual':
        return <Edit3 className="h-4 w-4 text-gray-500" />;
      case 'original':
        return <Clock className="h-4 w-4 text-green-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'enhanced':
        return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      case 'variation':
        return 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800';
      case 'manual':
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
      case 'original':
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return formatDate(date);
  };

  if (!isOpen) return null;

  const history = prompt.history || [];
  const hasHistory = history.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div ref={modalRef} className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center">
            <Clock className="h-6 w-6 text-fire-600 dark:text-fire-400 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">Prompt History</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Version history for "{prompt.title}"
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {!hasHistory ? (
            <div className="text-center py-12">
              <Clock className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2">
                No History Yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                This prompt hasn't been modified yet. History will appear here when you enhance, 
                edit, or apply variations to this prompt.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Current Version */}
              <div className="bg-modular-canvas-fog dark:bg-modular-shadow-quartz/20 border border-modular-stone-alloy dark:border-modular-graphite-smoke rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-fire-500 rounded-full mr-3"></div>
                    <h3 className="text-lg font-medium text-fire-700 dark:text-fire-300">
                      Current Version
                    </h3>
                    <span className="ml-3 text-xs px-2 py-1 bg-fire-200 dark:bg-fire-800 text-fire-700 dark:text-fire-300 rounded-full">
                      Active
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-fire-600 dark:text-fire-400">
                      {formatRelativeTime(prompt.updatedAt)}
                    </span>
                    <button
                      onClick={() => handleCopy(prompt.body, -1)}
                      className={`flex items-center px-3 py-1.5 text-sm rounded-lg transition-all duration-300 ${
                        copiedIndex === -1 // Keep this logic
                          ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                          : 'text-fire-600 dark:text-fire-400 hover:bg-fire-100 dark:hover:bg-fire-800/30'
                      }`}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      {copiedIndex === -1 ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
                <p className="text-gray-900 dark:text-dark-text text-sm leading-relaxed whitespace-pre-wrap">
                  {prompt.body}
                </p>
              </div>

              {/* History Timeline */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Previous Versions ({history.length})
                </h3>
                
                <div className="space-y-4">
                  {history.map((entry, index) => (
                    <div
                      key={entry.id}
                      className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg p-6 hover:shadow-lg transition-shadow duration-300"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <div className="flex items-center mr-4">
                            {getTypeIcon(entry.type)}
                            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Version {history.length - index}
                            </span>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full border ${getTypeColor(entry.type)}`}>
                            {entry.description || entry.type}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(entry.timestamp)}
                          </span>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleCopy(entry.body, index)}
                              className={`flex items-center px-3 py-1.5 text-sm rounded-lg transition-all duration-300 ${
                                copiedIndex === index // Keep this logic
                                  ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                                  : 'text-gray-600 dark:text-gray-300 hover:text-fire-600 dark:hover:text-fire-400 hover:bg-fire-50 dark:hover:bg-fire-900/20'
                              }`}
                            >
                              <Copy className="h-4 w-4 mr-1" />
                              {copiedIndex === index ? 'Copied!' : 'Copy'}
                            </button>
                            <button
                              onClick={() => handleRestore(entry)}
                              className="flex items-center px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 
                                hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 
                                dark:hover:bg-blue-900/20 rounded-lg transition-all duration-300"
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Restore
                            </button>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-900 dark:text-dark-text text-sm leading-relaxed whitespace-pre-wrap">
                        {entry.body}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};